/** Backend JSON shape for SSO/SPA errors (ACS, OIDC, handshake exchange). */
export interface SsoClientError {
  sso_error: number;
  sso_ref: string;
}

/** View model for callback page (query redirect or parsed HTTP error). */
export interface SsoContractErrorView {
  code: number;
  ref: string;
}

/** Reserved code for non-contract HTTP failures on exchange. */
export const SSO_EXCHANGE_UNEXPECTED = -1;

const KNOWN_SSO_ERROR_CODES: ReadonlySet<number> = new Set([
  100001, 100002, 100003, 100004, 100005, 100006, 100007, 100010, 100011, 100012, 100020, 100021, 100022, 100023,
]);

/**
 * Parses backend error body. `sso_ref` may be absent; normalized to empty string.
 */
export function parseSsoClientError(body: unknown): SsoClientError | null {
  if (!body || typeof body !== 'object') {
    return null;
  }
  const o = body as Record<string, unknown>;
  if (typeof o.sso_error !== 'number' || !Number.isFinite(o.sso_error)) {
    return null;
  }
  const ref = typeof o.sso_ref === 'string' ? o.sso_ref : '';
  return { sso_error: Math.trunc(o.sso_error), sso_ref: ref };
}

export function isSsoClientError(body: unknown): body is SsoClientError {
  return parseSsoClientError(body) !== null;
}

/**
 * i18n key: specific `sso.error.{code}`, generic for unknown stable codes, or exchange fallback.
 */
export function getSsoErrorMessageKey(code: number): string {
  if (code === SSO_EXCHANGE_UNEXPECTED) {
    return 'sso.error.exchangeFailed';
  }
  if (KNOWN_SSO_ERROR_CODES.has(code)) {
    return `sso.error.${code}`;
  }
  return 'sso.error.generic';
}
